import {
  AlertCircle,
  ArrowRight, Brain,
  Calendar, CheckCircle,
  Clock,
  FileText,
  Phone,
  Shield,
  Users
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { StatCard, YearVerificationAlert } from './components';
import { DashboardEventBanner } from '../announcements';

const StatusIndicator = ({ status }) => {
  const styles = {
    scheduled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800',
    'in-progress': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800',
    completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800',
    cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
  };

  return (
    <span className={`px-2.5 py-1 text-xs rounded-full font-semibold ${styles[status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};



export const StudentDashboard = () => {
  const { get } = useApi();
  const { user } = useAuth();
  const navigate = useNavigate();

  const isStudentOrAlumni = ['student', 'alumni'].includes(user?.role);
  const basePath = isStudentOrAlumni ? '/student' : '/staff';

  const [stats, setStats] = useState({
    upcomingAppointments: 0,
    completedSessions: 0,
    availableCounselors: 0,
    nextAppointment: null,
    recentSessions: [],
    urgentCareInfo: null,
    notifications: [],
    sessionProgress: {
      goalsSet: 0,
      goalsAchieved: 0,
      sessionsAttended: 0,
      totalSessions: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await get('/dashboard/stats/student');
        // Ensure we have default values for all properties
        setStats(prev => ({
          ...prev,
          ...response.data.data,
          sessionProgress: {
            ...prev.sessionProgress,
            ...(response.data.data.sessionProgress || {})
          }
        }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    // Set up periodic refresh
    const interval = setInterval(fetchStats, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, [get]);

  const handleBookAppointment = () => {
    navigate(`${basePath}/appointments/new`);
  };

  const handleViewSessions = () => {
    navigate(`${basePath}/sessions`);
  };

  const handleViewAppointmentDetails = (appointmentId) => {
    navigate(`${basePath}/appointments/${appointmentId}`);
  };

  // Calculate goals percentage safely
  const calculateGoalsPercentage = () => {
    const goalsSet = stats.sessionProgress?.goalsSet || 0;
    const goalsAchieved = stats.sessionProgress?.goalsAchieved || 0;
    if (goalsSet === 0) return '0';
    return Math.round((goalsAchieved / goalsSet) * 100).toString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200 dark:border-blue-900 border-t-blue-600 dark:border-t-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 rounded-r-xl">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="ml-3 text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Welcome Section */}
      <div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent">
          Welcome back, {user?.fullName || user?.username}!
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Track your counseling journey and upcoming sessions</p>
      </div>

      {/* Year Verification Alert (Smart Prompt) */}
      <YearVerificationAlert />

      {/* Event Announcements Banner */}
      <DashboardEventBanner />

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Upcoming Appointments"
          value={stats.upcomingAppointments || 0}
          icon={Calendar}
          description="Scheduled sessions"
        />
        <StatCard
          title="Sessions Completed"
          value={stats.completedSessions || 0}
          icon={CheckCircle}
          description="Total sessions attended"
        />
        <StatCard
          title="Available Counselors"
          value={stats.availableCounselors || 0}
          icon={Users}
          description="Ready to help"
        />
        <StatCard
          title="Goals Progress"
          value={`${calculateGoalsPercentage()}%`}
          icon={Brain}
          description={`${stats.sessionProgress?.goalsAchieved || 0}/${stats.sessionProgress?.goalsSet || 0} goals achieved`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Next Appointment */}
          {stats.nextAppointment && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 px-6 py-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-800">
                <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  Next Appointment
                </h2>
                <StatusIndicator status={stats.nextAppointment.status} />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white text-lg">
                      {stats.nextAppointment.counselorName}
                    </p>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 mt-2">
                      <Calendar className="h-4 w-4 mr-2" />
                      {new Date(stats.nextAppointment.date).toLocaleDateString()}
                      <Clock className="h-4 w-4 mx-2" />
                      {stats.nextAppointment.timeSlot}
                    </div>
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {stats.nextAppointment.serviceType}
                    </div>
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {stats.nextAppointment.sessionType}
                    </div>
                  </div>
                  <button
                    onClick={() => handleViewAppointmentDetails(stats.nextAppointment._id)}
                    className="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recent Sessions */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10 px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                Recent Sessions
              </h2>
            </div>
            <div className="p-6">
              {stats.recentSessions?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recentSessions.map(session => (
                    <div key={session._id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-800/50 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{session.counselorName}</p>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(session.date).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`${basePath}/sessions/${session._id}`)}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        View Notes
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full mb-4">
                    <FileText className="h-8 w-8 text-gray-400 dark:text-gray-600" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">No recent sessions</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Your session history will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-50/50 to-teal-50/50 dark:from-emerald-900/10 dark:to-teal-900/10 px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Quick Actions</h2>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={handleBookAppointment}
                className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Book New Appointment
              </button>
              <button
                onClick={handleViewSessions}
                className="w-full flex items-center justify-center px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                <FileText className="h-5 w-5 mr-2" />
                View Session History
              </button>
            </div>
          </div>

          {/* Urgent Care Info */}
          {stats.urgentCareInfo && (
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border-2 border-red-200 dark:border-red-900/50 overflow-hidden">
              <div className="bg-red-50 dark:bg-red-950/30 px-6 py-4 border-b border-red-200 dark:border-red-900/50">
                <h2 className="text-base font-bold text-red-700 dark:text-red-400 flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Urgent Care
                </h2>
              </div>
              <div className="p-6">
                <div className="flex items-center text-red-600 dark:text-red-400 mb-4">
                  <Shield className="h-5 w-5 mr-2" />
                  <span className="font-semibold">24/7 Support Available</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  {stats.urgentCareInfo.message}
                </p>
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-gray-900 dark:text-white">Emergency Contacts:</p>
                  {stats.urgentCareInfo.contacts?.map((contact, index) => (
                    <p key={index} className="flex items-center text-gray-600 dark:text-gray-300">
                      <Phone className="h-4 w-4 mr-2 text-red-500 dark:text-red-400" />
                      {contact}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;